import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Space, Tag } from '@douyinfe/semi-ui';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { projects } from './data/projects';
import {
  IconCode,
  IconServer,
  IconGlobe,
  IconLightningStroked,
  IconLock,
  IconApps,
} from '@douyinfe/semi-icons';
import { Heart, Activity, Users, Lightbulb, Shield } from "lucide-react"; 

import SocialSection from './common/SocialSection';

ChartJS.register( ArcElement, ChartTooltip, ChartLegend );

const fallbackCategories = {
  Healthcare: 30,
  Education: 25,
  'Social Support': 20,
  'Creative Tech': 15,
  'Future Tools': 10,
};

const categoryData = projects.reduce((acc, { project }) => {
  const category = project?.category?.name || 'Other';
  acc[category] = (acc[category] || 0) + 1;
  return acc;
}, {});

const finalCategoryData = Object.keys(categoryData).length ? categoryData : fallbackCategories;
const categoryLabels = Object.keys(finalCategoryData);
const categoryValues = Object.values(finalCategoryData);

const NEON_COLORS = [
  '#39bdd1', '#b16eae', '#55b7a0', '#648bb8', '#c39a69', '#719ba8',
];

const categoryTotal = categoryValues.reduce((sum, value) => sum + Number(value || 0), 0);
const categoryBreakdown = categoryLabels.map((label, index) => {
  const value = Number(categoryValues[index] || 0);
  return {
    label,
    value,
    percentage: categoryTotal ? (value / categoryTotal) * 100 : 0,
    color: NEON_COLORS[index % NEON_COLORS.length],
  };
}).sort((a, b) => b.value - a.value);

const doughnutData = {
  labels: categoryLabels,
  datasets: [{
    data: categoryValues,
    backgroundColor: categoryLabels.map((_, index) => NEON_COLORS[index % NEON_COLORS.length]),
    borderColor: '#0a1421',
    borderWidth: 3,
    hoverBorderColor: '#d5edf1',
    hoverBorderWidth: 3,
    hoverOffset: 3,
    spacing: 1,
    borderRadius: 2,
  }],
};

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '76%',
  radius: '87%',
  animation: false,
  devicePixelRatio: 1.5,
  events: ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove'],
  layout: { padding: 6 },
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: true,
      backgroundColor: '#0b1929',
      titleColor: '#ddf4f7',
      bodyColor: '#a9c1ce',
      borderColor: 'rgba(57,189,209,.45)',
      borderWidth: 1,
      cornerRadius: 6,
      padding: 12,
      displayColors: true,
      callbacks: {
        label: (context) => {
          const value = Number(context.raw || 0);
          const percent = categoryTotal ? (value / categoryTotal) * 100 : 0;
          return ` ${value} projects · ${percent.toFixed(1)}%`;
        },
      },
    },
  },
};

const supportCards = [
  {
    icon: <Activity />,
    title: 'Healthcare Access',
    text: 'Improving access to care through smart, inclusive tools.',
  },
  {
    icon: <Users />,
    title: 'Social Impact',
    text: 'Empowering communities and uplifting lives with technology.',
  },
  {
    icon: <Lightbulb />,
    title: 'Open Innovation',
    text: 'Building open, ethical and collaborative solutions.',
  },
  {
    icon: <Shield />,
    title: 'Long-Term Vision',
    text: 'Creating sustainable tools for a better tomorrow.',
  },
];

const projectCards = [
  {
    visual: <Activity />,
    tag: 'Healthcare',
    title: 'Medical Accessibility Tools',
    text: 'Tools and resources that help improve access to medical information and services.',
    variant: 'medical',
  },
  {
    visual: <IconLightningStroked />,
    tag: 'Technology',
    title: 'AI Assistant for Patients',
    text: 'An AI-powered assistant that supports patients with guidance, information and resources.',
    variant: 'ai',
  },
  {
    visual: <IconLock />,
    tag: 'Connectivity',
    title: 'Real-Time Communication Platform',
    text: 'A secure platform connecting people and organizations when it matters most.',
    variant: 'realtime',
  },
];

const fundingItems = [
  {
    icon: <IconCode />,
    title: 'Development',
    text: 'Build and improve core tools and features.',
  },
  {
    icon: <IconServer />,
    title: 'Infrastructure',
    text: 'Power reliable, secure and scalable systems.',
  },
  {
    icon: <Lightbulb />,
    title: 'Research',
    text: 'Invest in innovation and evidence-based solutions.',
  },
  {
    icon: <IconGlobe />,
    title: 'Accessibility',
    text: 'Make technology inclusive and accessible to all.',
  },
];

class HomePage extends React.Component {
  state = { selectedCategory: null };

  selectCategory = (label) => {
    this.setState((prev) => ({ selectedCategory: prev.selectedCategory === label ? null : label }));
  };

  goTo = (path) => {
    this.props.navigate(path);
  };

  renderSupportCard = ({ icon, title, text }) => (
    <div className="cr-support-card" key={title}>
      <div className="cr-support-icon">{icon}</div>

      <div className="cr-support-copy">
        <Typography.Title heading={4} className="cr-card-title">
          {title}
        </Typography.Title>

        <Typography.Paragraph className="cr-card-text">
          {text}
        </Typography.Paragraph>
      </div>
    </div>
  );

  renderProjectCard = ({ visual, tag, title, text, variant }) => (
    <div className={`cr-project-card cr-project-card-${variant}`} key={title}>
      <div className="cr-project-visual">
        <div className="cr-project-icon">
          {visual}
        </div>
      </div>

      <div className="cr-project-content">
        <Tag className="tag-custom tag-teal cr-project-tag">
          {tag}
        </Tag>

        <Typography.Title heading={4} className="cr-project-title">
          {title}
        </Typography.Title>

        <Typography.Paragraph className="cr-project-text">
          {text}
        </Typography.Paragraph>

        <Button
          theme="solid"
          type="primary"
          className="button-custom cr-small-button"
          onClick={() => this.goTo('/projects')}
        >
          <span>View Project</span>
          <span>→</span>
        </Button>
      </div>
    </div>
  );

  renderFundingItem = ({ icon, title, text }) => (
    <div className="cr-funding-item" key={title}>
      <div className="cr-funding-icon">{icon}</div>

      <div>
        <Typography.Title heading={5} className="cr-funding-title">
          {title}
        </Typography.Title>

        <Typography.Paragraph className="cr-funding-text">
          {text}
        </Typography.Paragraph>
      </div>
    </div>
  );

  render() {
    return (
      <div className="grid-container cr-landing">
        <div className="cr-page-shell">
          <section className="cr-hero" aria-label="CyberRose introduction">
            <div className="cr-hero-shade" />

            <div className="cr-hero-copy">
              <div className="cr-eyebrow">CYBERROSE ECOSYSTEM</div>

              <Typography.Title className="page-title cr-hero-title">
                Technology Projects<br />for a Better Future
              </Typography.Title>

              <Typography.Paragraph className="cr-hero-text">
                CyberRose is an ecosystem of independent projects focused on healthcare,
                education, social support, creative technology and future-oriented tools.
              </Typography.Paragraph>

              <Space className="cr-hero-actions">
                <Button
                  theme="solid"
                  type="primary"
                  className="button-custom cr-primary-button"
                  onClick={() => this.goTo('/support')}
                >
                  <span><Heart/></span>
                  <span>Support Projects</span>
                </Button>

                <Button
                  theme="solid"
                  type="secondary"
                  className="button-custom cr-secondary-button"
                  onClick={() => this.goTo('/projects')}
                >
                  <span>Explore Projects</span>
                  <span>→</span>
                </Button>
              </Space>

              <div className="cr-stat-row">
                <div className="cr-stat">
                  <span className="cr-stat-icon">
                    <IconApps />
                  </span>
                  <div>
                    <strong>12+</strong>
                    <span>active ideas</span>
                  </div>
                </div>

                <div className="cr-stat">
                  <span className="cr-stat-icon">
                    <IconServer />
                  </span>
                  <div>
                    <strong>Multiple</strong>
                    <span>categories</span>
                  </div>
                </div>

                <div className="cr-stat">
                  <span className="cr-stat-icon">
                    <Shield />
                  </span>
                  <div>
                    <strong>Independent</strong>
                    <span>initiative</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="cr-hero-corner" aria-hidden="true"><span>CR / 001</span><span>BUILDING THE FUTURE</span></div>
          </section>

          <section className="cr-section">
            <Typography.Title heading={3} className="cr-section-title">
                Why Support Matters
            </Typography.Title>

            <div className="cr-support-grid">
                {supportCards.map(this.renderSupportCard)}
            </div>
          </section>

          <section className="cr-section">
            <Typography.Title heading={3} className="cr-section-title">
                Current Projects Need Support
            </Typography.Title>

            <div className="cr-project-grid">
                {projectCards.map(this.renderProjectCard)}
            </div>
          </section>

          <section className="cr-section cr-wide-section cr-data-section">
            <Typography.Title heading={3} className="cr-section-title">
              Projects Across Categories
            </Typography.Title>

            <div className="cr-data-panel">
              <div className="cr-chart-panel">
                <div className="cr-chart-header">
                  <div>
                    <span className="cr-chart-eyebrow">CYBERROSE / INTELLIGENCE</span>
                    <h3 className="cr-chart-heading">ECOSYSTEM OVERVIEW</h3>
                  </div>
                  <span className="cr-chart-status"><span aria-hidden="true" /> PROJECT DATA</span>
                </div>
                <div className="cr-chart-content">
                  <div className="cr-chart-viz">
                    <div className="cr-chart-orbit">
                      <div className="cr-chart-wrap">
                        <Doughnut data={doughnutData} options={doughnutOptions} />
                      </div>
                      <div className="cr-chart-core" aria-live="polite">
                        <strong>{this.state.selectedCategory === null ? categoryTotal : categoryBreakdown.find((item) => item.label === this.state.selectedCategory)?.value ?? categoryTotal}</strong>
                        <span>{this.state.selectedCategory === null ? 'PROJECTS' : 'IN CATEGORY'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="cr-chart-breakdown" aria-label="Project category distribution">
                    {categoryBreakdown.map(({ label, value, percentage, color }, index) => (
                      <button
                        type="button"
                        className={`cr-breakdown-item cr-breakdown-button${this.state.selectedCategory === label ? ' is-selected' : ''}`}
                        key={label}
                        onClick={() => this.selectCategory(label)}
                        aria-pressed={this.state.selectedCategory === label}
                        aria-label={`${label}: ${value} projects, ${percentage.toFixed(1)} percent. Click to highlight.`}
                        style={{ '--cr-category-color': color }}
                      >
                        <div className="cr-breakdown-top">
                          <span className="cr-breakdown-index">{String(index + 1).padStart(2, '0')}</span>
                          <span className="cr-breakdown-name">{label}</span>
                          <strong>{value}</strong>
                        </div>
                        <div className="cr-breakdown-track">
                          <span style={{ width: `${percentage}%`, backgroundColor: color }} />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="cr-chart-footer">
                  <span>{this.state.selectedCategory ? `FOCUS / ${this.state.selectedCategory.toUpperCase()}` : 'SELECT A CATEGORY TO EXPLORE'}</span>
                  <span>{categoryBreakdown.length} CATEGORIES</span>
                </div>
              </div>

              <div className="cr-data-copy">
                <Typography.Title heading={3} className="cr-data-title">
                    A diverse ecosystem of purpose-driven projects.
                </Typography.Title>

                <Typography.Paragraph className="cr-data-text">
                    From healthcare and education to creative technology and future tools,
                    our projects are built to solve real problems and create lasting impact.
                </Typography.Paragraph>

                <div className="cr-mini-stats">
                  <div>
                    <strong>12+</strong>
                    <span>Active Ideas</span>
                  </div>

                  <div>
                    <strong>5+</strong>
                    <span>Categories</span>
                  </div>

                  <div>
                    <strong>Global</strong>
                    <span>Impact Focus</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="cr-section">
            <Typography.Title heading={3} className="cr-section-title">
                Funding Priorities
            </Typography.Title>

            <div className="cr-funding-panel">
                {fundingItems.map(this.renderFundingItem)}
            </div>
          </section>

          <section className="cr-final-cta">
            <div className="cr-final-heart">
              <Heart />
            </div>

            <div className="cr-final-copy">
              <Typography.Title heading={2} className="cr-final-title">
                Help turn meaningful ideas into real tools for people.
              </Typography.Title>

              <Typography.Paragraph className="cr-final-text">
                Support development, infrastructure, research and accessibility for projects
                built with purpose.
              </Typography.Paragraph>
            </div>

            <Space className="cr-final-actions">
              <Button
                theme="solid"
                type="primary"
                className="button-custom cr-primary-button"
                onClick={() => this.goTo('/support')}
                >
                <span><Heart/></span>
                <span>Donate Now</span>
                </Button>

                <Button
                theme="solid"
                type="secondary"
                className="button-custom cr-secondary-button"
                onClick={() => this.goTo('/contacts')}
                >
                <span>Partner With Us</span>
                <span>→</span>
              </Button>
            </Space>
          </section>

          <div className="cr-social-wrap">
            <SocialSection />
          </div>
        </div>
      </div>
    );
  }
}

export default function HomePageRoute() {
  const navigate = useNavigate();
  return <HomePage navigate={navigate} />;
}